import {
    type CSSProperties,
    type ReactNode,
    useEffect,
    useRef
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitLocalize,
    LocaleSwitcher
} from "ncore-ui-kit";
import {
    getKitLocale,
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Caption,
    Preview,
    Section
} from "../../components/content";

const NAME = "LocaleSwitcher";

const USAGE_CODE = `<LocaleSwitcher />

<LocaleSwitcher
    form="segment"
/>`;

const VARIANTS_CODE = `<LocaleSwitcher
    variants={[
        {
            nativeTitle: "Türkçe",
            iconColor: "turkish",
            localeKey: "tr-TR",
            title: "Turkish",
            shortTitle: "TUR"
        },
        {
            nativeTitle: "English",
            iconColor: "english",
            localeKey: "en-US",
            title: "English",
            shortTitle: "ENG"
        }
    ]}
    isShowTitle={true}
/>`;

const FORMS = [
    "cycle",
    "menu",
    "segment"
] as const;

const DEMO_ITEM_STYLE: CSSProperties = {
    flexDirection: "column",
    alignItems: "center",
    display: "flex"
};

const DEMO_CONTENT_STYLE: CSSProperties = {
    justifyContent: "center",
    display: "flex",
    width: "100%"
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("localeSwitcherPage.formsTitle"),
        id: "forms"
    },
    {
        title: t("localeSwitcherPage.displayTitle"),
        id: "display"
    },
    {
        title: t("localeSwitcherPage.variantsTitle"),
        id: "variants"
    },
    {
        title: t("localeSwitcherPage.styleTitle"),
        id: "style"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const DemoItem = ({
    children,
    label
}: {
    children: ReactNode;
    label: string;
}) => <div
    style={DEMO_ITEM_STYLE}
>
    <div
        style={DEMO_CONTENT_STYLE}
    >
        {children}
    </div>
    <Caption>
        {label}
    </Caption>
</div>;

const LocaleSwitcherPage = () => {
    const {
        language,
        t
    } = useDocsI18n();

    const languageRef = useRef(language);

    useEffect(() => {
        languageRef.current = language;
    }, [
        language
    ]);

    useEffect(() => {
        return () => {
            NCoreUIKitLocalize.switch({
                localeKey: getKitLocale(languageRef.current)
            });
        };
    }, []);

    return <Article>
        <ComponentHeader
            lead={t("localeSwitcherPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("localeSwitcherPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("localeSwitcherPage.overviewCaption")}
            >
                <LocaleSwitcher/>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("localeSwitcherPage.formsTitle")}
            id="forms"
        >
            <Paragraph>
                {t("localeSwitcherPage.formsText")}
            </Paragraph>
            <Preview>
                {
                    FORMS.map((form) => <DemoItem
                        label={form}
                        key={form}
                    >
                        <LocaleSwitcher
                            form={form}
                        />
                    </DemoItem>)
                }
            </Preview>
        </Section>
        <Section
            title={t("localeSwitcherPage.displayTitle")}
            id="display"
        >
            <Paragraph>
                {t("localeSwitcherPage.displayText")}
            </Paragraph>
            <Preview>
                <DemoItem
                    label="isShowTitle"
                >
                    <LocaleSwitcher
                        isShowTitle={true}
                    />
                </DemoItem>
                <DemoItem
                    label="title"
                >
                    <LocaleSwitcher
                        title={t("localeSwitcherPage.customTitle")}
                    />
                </DemoItem>
                <DemoItem
                    label="isShowNextVariant"
                >
                    <LocaleSwitcher
                        isShowNextVariant={true}
                        isShowTitle={true}
                    />
                </DemoItem>
            </Preview>
        </Section>
        <Section
            title={t("localeSwitcherPage.variantsTitle")}
            id="variants"
        >
            <Paragraph>
                {t("localeSwitcherPage.variantsText")}
            </Paragraph>
            <Preview>
                <LocaleSwitcher
                    variants={[
                        {
                            title: t("localeSwitcherPage.turkish"),
                            nativeTitle: "Türkçe",
                            iconColor: "turkish",
                            localeKey: "tr-TR",
                            shortTitle: "TUR"
                        },
                        {
                            title: t("localeSwitcherPage.english"),
                            nativeTitle: "English",
                            iconColor: "english",
                            localeKey: "en-US",
                            shortTitle: "ENG"
                        }
                    ]}
                    isShowTitle={true}
                />
            </Preview>
            <CodeBlock
                code={VARIANTS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("localeSwitcherPage.styleTitle")}
            id="style"
        >
            <Paragraph>
                {t("localeSwitcherPage.styleText")}
            </Paragraph>
            <Preview>
                <DemoItem
                    label="variant=outline"
                >
                    <LocaleSwitcher
                        variant="outline"
                    />
                </DemoItem>
                <DemoItem
                    label="variant=ghost"
                >
                    <LocaleSwitcher
                        variant="ghost"
                    />
                </DemoItem>
                <DemoItem
                    label="type=primary"
                >
                    <LocaleSwitcher
                        type="primary"
                    />
                </DemoItem>
                <DemoItem
                    label="color=raised"
                >
                    <LocaleSwitcher
                        variant="outline"
                        color="raised"
                    />
                </DemoItem>
                <DemoItem
                    label="color=subtle"
                >
                    <LocaleSwitcher
                        variant="outline"
                        color="subtle"
                    />
                </DemoItem>
                <DemoItem
                    label="color=transparent"
                >
                    <LocaleSwitcher
                        color="transparent"
                        variant="outline"
                    />
                </DemoItem>
                <DemoItem
                    label="size=large"
                >
                    <LocaleSwitcher
                        size="large"
                    />
                </DemoItem>
                <DemoItem
                    label="customTheme=light"
                >
                    <LocaleSwitcher
                        customTheme={{
                            themeKey: "light"
                        }}
                    />
                </DemoItem>
                <DemoItem
                    label="customTheme=dark"
                >
                    <LocaleSwitcher
                        customTheme={{
                            themeKey: "dark"
                        }}
                    />
                </DemoItem>
                <DemoItem
                    label="isDisabled"
                >
                    <LocaleSwitcher
                        isDisabled={true}
                    />
                </DemoItem>
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("localeSwitcherPage.a11yName"),
                    t("localeSwitcherPage.a11yLang"),
                    t("localeSwitcherPage.a11yLabelInName"),
                    t("localeSwitcherPage.a11yMenu"),
                    t("localeSwitcherPage.a11ySegment"),
                    t("localeSwitcherPage.a11yDirection"),
                    t("localeSwitcherPage.a11yDisabled"),
                    t("localeSwitcherPage.a11yCustom"),
                    t("localeSwitcherPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("localeSwitcherPage.propForm"),
                        type: "\"cycle\" | \"menu\" | \"segment\"",
                        defaultValue: "\"cycle\"",
                        name: "form"
                    },
                    {
                        description: t("localeSwitcherPage.propIsShowTitle"),
                        name: "isShowTitle",
                        type: "boolean"
                    },
                    {
                        description: t("localeSwitcherPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("localeSwitcherPage.propIsShowNextVariant"),
                        name: "isShowNextVariant",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        type: "Array<{ localeKey, title, nativeTitle, shortTitle, icon, iconColor, backgroundColor, borderColor }>",
                        description: t("localeSwitcherPage.propVariants"),
                        name: "variants"
                    },
                    {
                        type: "\"filled\" | \"tonal\" | \"outline\" | \"ghost\"",
                        description: t("localeSwitcherPage.propVariant"),
                        defaultValue: "\"tonal\"",
                        name: "variant"
                    },
                    {
                        type: "\"primary\" | \"danger\" | \"warning\" | \"neutral\" | \"success\" | \"info\"",
                        description: t("localeSwitcherPage.propType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        type: "\"xSmall\" | \"small\" | \"medium\" | \"large\"",
                        description: t("localeSwitcherPage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("localeSwitcherPage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"stretch\" | \"free\"",
                        defaultValue: "\"baseline\"",
                        name: "spreadBehaviour"
                    },
                    {
                        description: t("localeSwitcherPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        type: "keyof ContainerContentColors | \"transparent\"",
                        description: t("localeSwitcherPage.propColor"),
                        name: "color"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("localeSwitcherPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("localeSwitcherPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    },
                    {
                        description: t("localeSwitcherPage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("localeSwitcherPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default LocaleSwitcherPage;
